import { useEffect, useState } from "react";
import { useCreateUser, useUpdateUser, useUser } from "http-hooks/users/useUsers";
import Input from "components/ui/Input";
import Select from "components/ui/Select";
import SelectGodown from "components/ui/selectors/SelectGodown";
import Modal from "components/ui/modals";

const levelOptions = [
  { value: 1, label: "Level 1" },
  { value: 2, label: "Level 2" },
  { value: 3, label: "Level 3" },
  { value: 4, label: "Level 4" },
  { value: 5, label: "Level 5" },
];

const statusOptions = [
  { value: 1, label: "Active" },
  { value: 0, label: "Inactive" },
];

export default function SetUser({ id, onClose, open }) {
  const isEditMode = !!id;
  const { mutateAsync: mutateCreate, isPending: isCreating } = useCreateUser();
  const { mutateAsync: mutateUpdate, isPending: isUpdating } = useUpdateUser();
  const { data: userData } = useUser(id);

  const isPending = isCreating || isUpdating;

  const [formData, setFormData] = useState({
    name: "",
    username: "",
    password: "",
    level: 1,
    godown: 0,
    flag: 1,
  });

  const [selectedGodown, setSelectedGodown] = useState({ value: 0, label: "All Godowns" });

  useEffect(() => {
    if (userData) {
      setFormData({
        name: userData.name || "",
        username: userData.username || "",
        password: "",
        level: userData.level || 1,
        godown: userData.godown !== undefined ? userData.godown : 0,
        flag: userData.flag !== undefined ? userData.flag : 1,
      });
      setSelectedGodown(
        userData.godown && userData.godown > 0
          ? { value: userData.godown, label: `Godown #${userData.godown}` }
          : { value: 0, label: "All Godowns" }
      );
    }
  }, [userData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.username.trim()) return;
    if (!isEditMode && !formData.password.trim()) return;

    try {
      const submitData = { ...formData };
      if (isEditMode && !submitData.password) delete submitData.password;

      if (isEditMode) {
        await mutateUpdate({ id, data: submitData });
      } else {
        await mutateCreate(submitData);
      }
      onClose();
    } catch (error) {
      console.error("Operation failed:", error);
    }
  };

  const selectedLevel = levelOptions.find((opt) => opt.value === formData.level);
  const selectedStatus = statusOptions.find((opt) => opt.value === formData.flag);

  return (
    <Modal
      open={open}
      setOpen={(val) => !val && onClose()}
      header={
        <h3 className="text-base font-semibold leading-6 text-gray-900">
          {isEditMode ? "Edit User" : "Add New User"}
        </h3>
      }
      footer={
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isPending}
            className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="set-user-form"
            disabled={isPending}
            className="rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPending ? "Saving..." : "Save"}
          </button>
        </div>
      }
    >
      <form id="set-user-form" onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Name *"
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="Enter full name"
          maxLength={40}
          required
        />
        <Input
          label="Username *"
          type="text"
          value={formData.username}
          onChange={(e) => setFormData({ ...formData, username: e.target.value })}
          placeholder="Enter username"
          maxLength={10}
          required
        />
        <Input
          label={isEditMode ? "Password (leave blank to keep current)" : "Password *"}
          type="password"
          value={formData.password}
          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
          placeholder="Enter password"
          required={!isEditMode}
        />
        <Select
          label="Level *"
          options={levelOptions}
          value={selectedLevel}
          onChange={(option) => setFormData({ ...formData, level: option.value })}
          placeholder="Select level"
          isClearable={false}
        />
        <SelectGodown
          label="Godown *"
          value={selectedGodown}
          onChange={(option) => {
            setSelectedGodown(option);
            setFormData({ ...formData, godown: option ? option.value : 0 });
          }}
          isClearable={false}
        />
        {isEditMode && (
          <Select
            label="Status *"
            options={statusOptions}
            value={selectedStatus}
            onChange={(option) => setFormData({ ...formData, flag: option.value })}
            placeholder="Select status"
            isClearable={false}
          />
        )}
      </form>
    </Modal>
  );
}
